import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/contexts/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft, ShieldCheck, ChevronDown, Users, User as UserIcon, Mail, MapPin,
  FileText, Calendar as CalIcon, Loader2, CheckCircle2, X, Sparkles, BookmarkCheck,
} from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function tomorrow7pm() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(19, 0, 0, 0);
  return d;
}

function fmtDate(d, locale) {
  return d.toLocaleDateString(locale || undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}
function fmtTime(d, locale) {
  return d.toLocaleTimeString(locale || undefined, { hour: 'numeric', minute: '2-digit' });
}

function Pill({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="rounded-full transition-all hover:brightness-95"
      style={{
        background: active ? 'rgba(16,185,129,0.10)' : '#F4F4F5',
        border: `1px solid ${active ? '#10B981' : '#F4F4F5'}`,
        color: active ? '#047857' : '#374151',
        padding: '7px 12px', fontSize: 12, fontWeight: 700,
      }}
    >
      {label}
    </button>
  );
}

function SavedIdeasPicker({ matchId, onPick }) {
  // Fills the "where" input with a saved AI Date Planner idea. VIP-only —
  // silently hides when the user has no saved ideas or is not VIP (403).
  const [ideas, setIdeas] = useState([]);
  useEffect(() => {
    if (!matchId) { setIdeas([]); return; }
    let cancel = false;
    api.get('/date-ideas/saved', { params: { match_id: matchId } })
      .then((r) => { if (!cancel) setIdeas(r.data?.ideas || []); })
      .catch(() => { if (!cancel) setIdeas([]); });
    return () => { cancel = true; };
  }, [matchId]);
  if (!ideas.length) return null;
  return (
    <div className="mb-2" data-testid="safe-meet-saved-ideas">
      <div className="flex items-center gap-1.5 mb-2 text-[11px] font-[800] tracking-[0.5px] uppercase text-[#6B7280]">
        <Sparkles size={12} className="text-[#D9A748]" /> Saved date ideas
      </div>
      <div className="flex flex-wrap gap-1.5">
        {ideas.map((idea) => (
          <button
            key={idea.title}
            type="button"
            onClick={() => onPick(idea)}
            data-testid={`safe-meet-saved-idea-${idea.title.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-[700] bg-white text-[#0F0F10] transition-colors hover:bg-[#F4F5F8]"
            style={{ border: '1px solid rgba(15,15,16,0.08)' }}
          >
            <BookmarkCheck size={11} className="text-[#0F0F10]" />
            <span className="truncate max-w-[180px]">{idea.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}


function SectionLabel({ children }) {
  return (
    <div
      className="uppercase font-black mt-6 mb-2"
      style={{ color: '#6B7280', fontSize: 11, letterSpacing: 1.5 }}
    >
      {children}
    </div>
  );
}

function FieldTile({ icon: Icon, children, onClick, testid, rightIcon: RightIcon }) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-testid={testid}
      className="w-full flex items-center gap-2.5 text-left"
      style={{
        padding: '12px 14px',
        background: '#FFFFFF', borderRadius: 12,
        border: '1px solid #E5E7EB',
      }}
    >
      {Icon && <Icon size={16} style={{ color: '#6B7280' }} />}
      <div className="flex-1 min-w-0">{children}</div>
      {RightIcon && <RightIcon size={16} style={{ color: '#9CA3AF' }} />}
    </Wrapper>
  );
}

function MatchPickerSheet({ open, matches, selectedId, onSelect, onClose }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[95] flex items-end justify-center"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white"
        style={{ borderTopLeftRadius: 24, borderTopRightRadius: 24, paddingBottom: 24 }}
      >
        {/* Grab handle */}
        <div className="pt-2 pb-1 flex items-center justify-center">
          <div style={{ width: 36, height: 4, borderRadius: 999, background: '#D4D4D8' }} />
        </div>
        <div className="px-5 pb-2 pt-3">
          <h3 className="font-black" style={{ fontSize: 17 }}>{t('safeMeet.modalTitle')}</h3>
        </div>
        <div className="px-3" style={{ maxHeight: 380, overflowY: 'auto' }}>
          {matches.length === 0 ? (
            <p className="px-3 py-6 text-center" style={{ color: '#B45309', fontSize: 13 }}>
              {t('safeMeet.modalNoMatches')}
            </p>
          ) : (
            matches.map((m) => {
              const u = m.other_user || m.user || {};
              const active = m.match_id === selectedId;
              return (
                <button
                  key={m.match_id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => onSelect(m)}
                  data-testid={`safe-meet-match-row-${m.match_id}`}
                  className="w-full flex items-center gap-3 rounded-xl text-left"
                  style={{
                    padding: '10px 12px',
                    background: active ? 'rgba(16,185,129,0.08)' : 'transparent',
                  }}
                >
                  {u.photo_url ? (
                    <img src={u.photo_url} alt="" className="rounded-full object-cover"
                         style={{ width: 44, height: 44 }} />
                  ) : (
                    <div className="rounded-full flex items-center justify-center"
                         style={{ width: 44, height: 44, background: '#F4F4F5', color: '#9CA3AF' }}>
                      <UserIcon size={20} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div style={{ color: '#111827', fontSize: 15, fontWeight: 700 }}>
                      {u.display_name || 'Match'}
                    </div>
                    {u.age && (
                      <div style={{ color: '#6B7280', fontSize: 12 }}>{u.age}</div>
                    )}
                  </div>
                  {active && <CheckCircle2 size={20} style={{ color: '#10B981' }} />}
                </button>
              );
            })
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full text-center mt-3"
          style={{ color: '#6B7280', fontSize: 14, fontWeight: 700 }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function SafeMeetNew() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [matchId, setMatchId] = useState('');
  const [dateTime, setDateTime] = useState(() => tomorrow7pm());
  const [location, setLocation] = useState('');
  const [trustedName, setTrustedName] = useState('');
  const [trustedEmail, setTrustedEmail] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [errAlert, setErrAlert] = useState(null); // {title, body}
  const [successMsg, setSuccessMsg] = useState(''); // when set → Alert with Done

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/matches?limit=100');
        const list = (r.data.matches || []).filter((m) => !!m && !!m.other_user);
        setMatches(list);
      } catch (_e) {
        setMatches([]);
      }
    })();
  }, []);

  const selectedMatch = useMemo(
    () => matches.find((m) => m.match_id === matchId),
    [matches, matchId]
  );

  const canSubmit =
    !!matchId &&
    !!location.trim() &&
    !!trustedName.trim() &&
    EMAIL_RE.test(trustedEmail.trim()) &&
    !submitting;

  const adjustDay = (delta) => {
    const d = new Date(dateTime);
    d.setDate(d.getDate() + delta);
    d.setSeconds(0, 0);
    setDateTime(d);
  };
  const setHour = (h) => {
    const d = new Date(dateTime);
    d.setHours(h, 0, 0, 0);
    setDateTime(d);
  };

  const submit = async () => {
    if (!canSubmit) return;
    if (dateTime.getTime() - Date.now() < 60_000) {
      setErrAlert({ title: t('safeMeet.pickFutureTitle'), body: t('safeMeet.pickFutureBody') });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        match_id: matchId,
        date_time: dateTime.toISOString(),
        location: location.trim().slice(0, 120),
        trusted_contact_name: trustedName.trim().slice(0, 60),
        trusted_contact_email: trustedEmail.trim().slice(0, 120),
        notes: notes.trim().slice(0, 300),
      };
      const res = await api.post('/safe-meet', payload);
      const matchName = selectedMatch?.other_user?.display_name || t('safeMeet.createSuccessFallbackMatch');
      const first = trustedName.trim().split(/\s+/)[0] || t('safeMeet.createSuccessFallbackName');
      setSuccessMsg(
        res?.data?.message ||
        t('safeMeet.createSuccessBody', { first, matchName })
      );
    } catch (_e) {
      setErrAlert({ title: t('safeMeet.createFailedTitle'), body: t('safeMeet.createFailedBody') });
    } finally {
      setSubmitting(false);
    }
  };

  const currentHour = dateTime.getHours();

  return (
    <div className="min-h-screen bg-white pb-[120px]" data-testid="safe-meet-new-page">
      {/* Header */}
      <div className="px-5 pt-6 pb-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          data-testid="safe-meet-back"
          aria-label={t('safeMeet.back')}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-zinc-100"
        >
          <ArrowLeft size={20} style={{ color: '#111827' }} />
        </button>
        <h1 className="flex-1 text-center font-black" style={{ color: '#111827', fontSize: 17 }}>
          {t('safeMeet.newHeaderTitle')}
        </h1>
        <div style={{ width: 32 }} />
      </div>

      <div className="px-5" data-testid="safe-meet-screen">
        {/* Hero */}
        <div
          className="rounded-[20px] mt-2"
          style={{
            background: 'linear-gradient(135deg, #10B981 0%, #047857 100%)',
            padding: 20,
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={22} className="text-white" strokeWidth={2} />
            <h2 className="text-white font-black" style={{ fontSize: 18 }}>{t('safeMeet.newHeroTitle')}</h2>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, lineHeight: 1.5 }}>
            {t('safeMeet.newHeroSub')}
          </p>
        </div>

        {/* WHO */}
        <SectionLabel>{t('safeMeet.sectionWho')}</SectionLabel>
        <FieldTile
          icon={selectedMatch ? null : Users}
          onClick={() => setPickerOpen(true)}
          testid="safe-meet-pick-match"
          rightIcon={ChevronDown}
        >
          {selectedMatch ? (
            <div className="flex items-center gap-2.5">
              {selectedMatch.other_user?.photo_url ? (
                <img
                  src={selectedMatch.other_user.photo_url}
                  alt=""
                  className="rounded-full object-cover"
                  style={{ width: 32, height: 32 }}
                />
              ) : (
                <div className="rounded-full flex items-center justify-center"
                     style={{ width: 32, height: 32, background: '#F4F4F5', color: '#9CA3AF' }}>
                  <UserIcon size={16} />
                </div>
              )}
              <span style={{ color: '#111827', fontSize: 15, fontWeight: 700 }}>
                {selectedMatch.other_user?.display_name}
              </span>
            </div>
          ) : (
            <span style={{ color: '#9CA3AF', fontSize: 15 }}>{t('safeMeet.pickMatchPh')}</span>
          )}
        </FieldTile>
        {matches.length === 0 && (
          <p className="mt-2" style={{ color: '#B45309', fontSize: 12.5 }}>
            {t('safeMeet.noMatchesHelper')}
          </p>
        )}

        {/* WHEN */}
        <SectionLabel>{t('safeMeet.sectionWhen')}</SectionLabel>
        <div
          className="rounded-xl"
          style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', padding: '14px' }}
        >
          <div className="flex items-center gap-2 mb-3">
            <CalIcon size={18} style={{ color: '#B91C1C' }} strokeWidth={2.5} />
            <div style={{ color: '#111827', fontSize: 17, fontWeight: 900 }}>
              {fmtDate(dateTime, i18n.language)} · {fmtTime(dateTime, i18n.language)}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Pill label={t('safeMeet.dayMinus')} onClick={() => adjustDay(-1)} testid="safe-meet-day-minus" />
            <Pill label={t('safeMeet.dayPlus')}  onClick={() => adjustDay(1)}  testid="safe-meet-day-plus" />
            <Pill label={t('safeMeet.weekPlus')} onClick={() => adjustDay(7)}  testid="safe-meet-week-plus" />
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {[18, 19, 20, 21].map((h) => (
              <Pill
                key={h}
                label={`${h - 12}pm`}
                active={currentHour === h}
                onClick={() => setHour(h)}
                testid={`safe-meet-hour-${h - 12}pm`}
              />
            ))}
          </div>
        </div>

        {/* WHERE */}
        <SectionLabel>{t('safeMeet.sectionWhere')}</SectionLabel>
        {/* Saved date ideas picker (VIP) — auto-fills the location field with
            the saved idea's title. Loaded for the currently-picked match only. */}
        <SavedIdeasPicker matchId={matchId} onPick={(idea) => setLocation(idea.title)} />
        <FieldTile icon={MapPin}>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            maxLength={120}
            placeholder={t('safeMeet.wherePh')}
            data-testid="safe-meet-location"
            className="w-full outline-none bg-transparent"
            style={{ color: '#111827', fontSize: 15 }}
          />
        </FieldTile>

        {/* CONTACT */}
        <SectionLabel>{t('safeMeet.sectionContact')}</SectionLabel>
        <div className="flex flex-col gap-2">
          <FieldTile icon={UserIcon}>
            <input
              type="text"
              value={trustedName}
              onChange={(e) => setTrustedName(e.target.value)}
              maxLength={60}
              placeholder={t('safeMeet.namePh')}
              autoCapitalize="words"
              data-testid="safe-meet-contact-name"
              className="w-full outline-none bg-transparent"
              style={{ color: '#111827', fontSize: 15 }}
            />
          </FieldTile>
          <FieldTile icon={Mail}>
            <input
              type="email"
              value={trustedEmail}
              onChange={(e) => setTrustedEmail(e.target.value)}
              maxLength={120}
              placeholder={t('safeMeet.emailPh')}
              autoCapitalize="off"
              autoCorrect="off"
              data-testid="safe-meet-contact-email"
              className="w-full outline-none bg-transparent"
              style={{ color: '#111827', fontSize: 15 }}
            />
          </FieldTile>
        </div>

        {/* NOTES */}
        <SectionLabel>{t('safeMeet.sectionNotes')}</SectionLabel>
        <div
          className="rounded-xl flex items-start gap-2.5"
          style={{ background: '#FFFFFF', border: '1px solid #E5E7EB', padding: '12px 14px' }}
        >
          <FileText size={16} style={{ color: '#6B7280', marginTop: 2 }} />
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={300}
            placeholder={t('safeMeet.notesPh')}
            data-testid="safe-meet-notes"
            className="w-full outline-none bg-transparent resize-none"
            style={{ color: '#111827', fontSize: 15, minHeight: 60 }}
          />
        </div>
      </div>

      {/* Sticky submit footer */}
      <div
        className="fixed left-0 right-0 bottom-0"
        style={{ background: '#FFFFFF', borderTop: '1px solid #E5E7EB', padding: 12 }}
      >
        <div className="max-w-md mx-auto">
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            data-testid="safe-meet-submit"
            className="w-full rounded-2xl flex items-center justify-center gap-2 transition-all"
            style={{
              background: 'linear-gradient(90deg, #10B981 0%, #047857 100%)',
              padding: '16px', opacity: canSubmit ? 1 : 0.4,
              boxShadow: '0 8px 24px -8px rgba(4,120,87,0.5)',
            }}
          >
            {submitting ? (
              <Loader2 size={18} className="text-white animate-spin" />
            ) : (
              <ShieldCheck size={18} className="text-white" strokeWidth={2.5} />
            )}
            <span className="text-white" style={{ fontSize: 16, fontWeight: 900, letterSpacing: 0.3 }}>
              {t('safeMeet.submitBtn')}
            </span>
          </button>
        </div>
      </div>

      {/* Success alert */}
      <AlertDialog open={!!successMsg} onOpenChange={(o) => { if (!o) { setSuccessMsg(''); navigate('/safe-meet'); } }}>
        <AlertDialogContent data-testid="safe-meet-success-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('safeMeet.createSuccessTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{successMsg}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => { setSuccessMsg(''); navigate('/safe-meet'); }}
              data-testid="safe-meet-success-done"
              style={{ background: '#10B981' }}
            >
              {t('safeMeet.done')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Error alert */}
      <AlertDialog open={!!errAlert} onOpenChange={(o) => !o && setErrAlert(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{errAlert?.title}</AlertDialogTitle>
            <AlertDialogDescription>{errAlert?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setErrAlert(null)}>{t('safeMeet.ok')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Match picker */}
      <MatchPickerSheet
        open={pickerOpen}
        matches={matches}
        selectedId={matchId}
        onSelect={(m) => { setMatchId(m.match_id); setPickerOpen(false); }}
        onClose={() => setPickerOpen(false)}
      />
    </div>
  );
}
